// Screen wake lock: the phone must not fall asleep on the music stand.
//
// The failure this prevents is specific (user-found at a jam): prop the phone
// up, read the chart, touch nothing for a minute — and the screen locks in
// the middle of the tune. Reading music is exactly the use an OS idle timer
// cannot see, so the app asks the platform to hold the screen on while it is
// visible — the same request iReal Pro and every stand app makes, and it
// needs no native wrapper: this is the web platform's Screen Wake Lock API
// (Chrome/Edge 84+, Safari 16.4+, Firefox 126+; absent, the app behaves as
// before and the screen sleeps).
//
// Platform facts the controller's shape encodes:
// - The UA RELEASES the lock on its own whenever the page loses visibility
//   (tab switch, phone pocketed). That is wanted — the lock only spends
//   battery while a chart is actually readable — but it means the lock must
//   be RE-ACQUIRED on every return to visibility, never held as one sentinel
//   forever. The release event is what keeps our reference honest.
// - `request()` is async and can REJECT (battery saver, permissions policy,
//   a request made while hidden). A rejection is a normal answer, never an
//   error to surface: the app works identically, the screen just sleeps.
// - iOS Safari in particular is known to decline a request made OUTSIDE a
//   user gesture (the boot-time request from App mount), while granting the
//   same request from inside one — so every tap retries, synchronously in
//   the pointerdown handler where the user activation is live. Retries are
//   event-driven only (visibility, taps); never a loop.
// - Because requests are async, a stale resolve can land after the caller
//   turned the lock off (or unmounted). The generation counter releases
//   those on arrival instead of leaking a held lock.
// - Requests can OVERLAP: a tap or a return to visibility while one is still
//   in flight sends another (deliberately — a tap during the boot request's
//   round-trip is the in-gesture request iOS will grant). Only the first
//   grant is kept; a later one is released on arrival, because the screen
//   stays on while ANY sentinel lives and turning the switch off can only
//   release the one it knows about.
//
// The status channel exists because a silent best-effort lock is
// indistinguishable from a broken one from a music stand (the FeelCuePicker
// lesson: a control must NAME what is actually in force): the Device row
// reads the live status and can say "holding" or "declined".

import { useEffect, useSyncExternalStore } from 'react';

/** The slice of WakeLockSentinel the controller needs — structural, so tests
 * can fake it and older TS DOM libs don't gate the build. */
export type WakeLockSentinelLike = {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
};

export type WakeLockNavigatorLike = {
  wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> };
};

export type WakeLockDocumentLike = {
  visibilityState: string;
  addEventListener(type: 'visibilitychange' | 'pointerdown', listener: () => void): void;
  removeEventListener(type: 'visibilitychange' | 'pointerdown', listener: () => void): void;
};

export function wakeLockSupported(
  nav: WakeLockNavigatorLike | undefined = typeof navigator !== 'undefined'
    ? (navigator as WakeLockNavigatorLike)
    : undefined,
): boolean {
  return Boolean(nav?.wakeLock);
}

/**
 * What the lock is actually doing right now. 'declined' is the actionable
 * one — the UA said no (Low Power Mode, or an out-of-gesture request on
 * iOS), and the next tap or return to visibility retries. 'waiting' means
 * enabled but the UA took the lock back (hidden page, battery saver) and a
 * re-acquire is pending. An attempt in flight deliberately reports nothing.
 */
export type WakeLockStatus = 'off' | 'waiting' | 'held' | 'declined';

export type WakeLockController = {
  /** Want the screen held on? Idempotent; acquisition is best-effort. */
  setEnabled(enabled: boolean): void;
  /** Release everything and stop listening. The controller is dead after. */
  dispose(): void;
};

export function createWakeLockController(
  nav: WakeLockNavigatorLike = navigator as WakeLockNavigatorLike,
  doc: WakeLockDocumentLike = document,
  onStatus: (status: WakeLockStatus) => void = () => undefined,
): WakeLockController {
  let enabled = false;
  let sentinel: WakeLockSentinelLike | null = null;
  let generation = 0;

  // NB acquire publishes NOTHING synchronously: it runs inside pointerdown,
  // and a status publish re-renders subscribers — React swallows a controlled
  // checkbox's toggle when its panel re-renders between pointerdown and click
  // (the avoid-b9 drawer switch, caught by e2e: every tap under a persistent
  // denial flickered waiting→declined and ate the click it rode in on).
  const acquire = () => {
    const lock = nav.wakeLock;
    if (!lock || !enabled || sentinel || doc.visibilityState !== 'visible') return;
    const requestGeneration = generation;
    lock.request('screen').then(
      (acquired) => {
        if (requestGeneration !== generation || sentinel) {
          // Turned off (or disposed) while the request was in flight, or an
          // overlapping request already holds the lock — release the spare
          // rather than leaking it held.
          void acquired.release().catch(() => undefined);
          return;
        }
        sentinel = acquired;
        onStatus('held');
        // The UA releases on its own (hidden tab, battery saver). Forgetting
        // the dead sentinel is what lets the next visibility change re-acquire.
        acquired.addEventListener('release', () => {
          if (sentinel === acquired) {
            sentinel = null;
            onStatus('waiting');
          }
        });
      },
      () => {
        // Denied is a normal answer — the screen just sleeps, and the next
        // tap or visibility change retries. Named so the UI can say why. A
        // spare refused while an overlapping request holds the lock says
        // nothing: 'declined' would start the media fallback beside it.
        if (requestGeneration === generation && enabled && !sentinel) onStatus('declined');
      },
    );
  };

  const onVisibilityChange = () => {
    if (doc.visibilityState === 'visible') acquire();
  };
  // A tap carries the user activation an out-of-gesture request lacked on
  // iOS; acquire() is a cheap no-op while the lock is already held.
  const onInteraction = () => acquire();
  doc.addEventListener('visibilitychange', onVisibilityChange);
  doc.addEventListener('pointerdown', onInteraction);

  const releaseHeld = () => {
    generation += 1; // strands any request still in flight
    const held = sentinel;
    sentinel = null;
    onStatus('off');
    if (held) void held.release().catch(() => undefined);
  };

  return {
    setEnabled(next: boolean) {
      if (next === enabled) return;
      enabled = next;
      if (next) acquire();
      else releaseHeld();
    },
    dispose() {
      doc.removeEventListener('visibilitychange', onVisibilityChange);
      doc.removeEventListener('pointerdown', onInteraction);
      enabled = false;
      releaseHeld();
    },
  };
}

// --- live status, readable anywhere (the Device row prints it) ---

let liveStatus: WakeLockStatus = 'off';
const statusListeners = new Set<() => void>();

function publishStatus(status: WakeLockStatus): void {
  if (status === liveStatus) return;
  liveStatus = status;
  statusListeners.forEach((listener) => listener());
}

/** The app-wide lock status — fed by the one useWakeLock mount in App. */
export function useWakeLockStatus(): WakeLockStatus {
  return useSyncExternalStore(
    (onChange) => {
      statusListeners.add(onChange);
      return () => statusListeners.delete(onChange);
    },
    () => liveStatus,
  );
}

/** Hold the screen awake while `enabled` (and the API exists). */
export function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !wakeLockSupported()) return;
    // Publishes are deferred a macrotask: a subscriber re-render landing
    // inside a tap's pointerdown→click window makes React revert controlled
    // checkboxes elsewhere in the app (see the note on acquire). Order is
    // preserved — equal-delay timeouts run FIFO.
    const controller = createWakeLockController(undefined, undefined, (status) => {
      setTimeout(() => publishStatus(status), 0);
    });
    controller.setEnabled(true);
    return () => controller.dispose();
  }, [enabled]);
}
